import clsx from 'clsx'
import React, { forwardRef, useContext, useId } from 'react'

import { type IconProp } from 'slab/types'

import { RadioGroupContext } from './RadioGroup'
import { RadioInput } from './RadioInput'

export interface BorderedRadioButtonTypes
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> {
  children?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  icon?: IconProp;
  label: React.ReactNode;
  name?: string;
  size?: 'small' | 'large';
}

export const BorderedRadioButton = forwardRef<
  HTMLInputElement,
  BorderedRadioButtonTypes
>(({ children, description, icon: Icon, id, label, name, size = 'large', ...props }, forwardedRef) => {
  const autogeneratedId = useId()
  const inputId = id || autogeneratedId

  const { name: radioGroupName } = useContext(RadioGroupContext)
  const appliedName = radioGroupName ?? name

  return (
    <label
      className={clsx(
        'group/radio-container cursor-pointer has-[:disabled]:cursor-default rounded-lg border border-gray-200 has-[:checked]:border-thatch-500 has-[:disabled]:border-gray-200 bg-white hover:bg-gray-50 has-[:focus-visible]:bg-gray-50 has-[:disabled]:bg-gray-100 has-[:checked]:bg-thatch-50 has-[:focus-visible]:has-[:checked]:bg-thatch-50 outline outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-thatch-500 transition shadow hover:shadow-md has-[:disabled]:shadow space-y-4',
        {
          'p-4 h-full block': size === 'large',
          'px-3 h-10 inline-flex items-center': size === 'small',
        }
      )}
    >
      <div className='flex'>
        <div className='h-5 flex items-center'>
          {Icon
            ? (
              <>
                <input
                  {...props}
                  className='sr-only peer'
                  id={inputId}
                  name={appliedName}
                  ref={forwardedRef}
                  type='radio'
                />
                <span className='text-gray-500 peer-checked:text-thatch-500'>
                  <Icon size={16} />
                </span>
              </>
              )
            : (
              <RadioInput
                {...props}
                id={inputId}
                name={appliedName}
                ref={forwardedRef}
              />
              )}
        </div>
        <div className='space-y-2 relative'>
          <div className='pl-3 body text-gray-800 group-has-[:disabled]:text-gray-500'>
            {label}
          </div>
          {size === 'large' && !!description && !children && (
            <div className='pl-3 text-gray-500 body-sm leading-normal'>
              {description}
            </div>
          )}
        </div>
      </div>
      {size === 'large' && children && (
        <>
          <div>{children}</div>
          {!!description && (
            <div className='text-gray-500 body-sm leading-normal'>
              {description}
            </div>
          )}
        </>
      )}
    </label>
  )
})
